/**
 * useShadowBatchMutations Hook
 *
 * Provides mutations for adjusting shadow batches in the Planner.
 * Allows the manager to tweak quantity, dates, or manually add/remove
 * shadow batches before approval.
 *
 * US-C2-006: Planner Page UI
 */

import { useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

/**
 * Hook for shadow batch mutations
 *
 * @returns {Object} {
 *   adjustQuantity: Function(batchId, newBlocks) → Promise<void>,
 *   shiftDate: Function(batchId, newDate) → Promise<void>,
 *   removeBatch: Function(batchId) → Promise<void>,
 *   addManualBatch: Function(strainId, weekStart, blocks) → Promise<Object>
 * }
 */
export function useShadowBatchMutations() {
  /**
   * Adjust the block count for a shadow batch
   * Marks the batch as manually adjusted.
   *
   * @param {string} batchId - UUID of the shadow batch
   * @param {number} newBlocks - New total_blocks value
   * @returns {Promise<void>}
   */
  const adjustQuantity = useCallback(async (batchId, newBlocks) => {
    try {
      const { error: updateError } = await supabase
        .from('production_batches')
        .update({
          total_blocks: newBlocks,
          manually_adjusted: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', batchId)
        .eq('is_shadow', true)

      if (updateError) throw updateError

      toast.success('Batch quantity updated', {
        description: `Set to ${newBlocks} block${newBlocks !== 1 ? 's' : ''}`
      })
    } catch (err) {
      console.error('[useShadowBatchMutations] Adjust quantity error:', err)
      toast.error('Failed to update quantity', { description: err.message })
      throw err
    }
  }, [])

  /**
   * Shift the inoculation date for a shadow batch
   * Marks the batch as manually adjusted.
   *
   * @param {string} batchId - UUID of the shadow batch
   * @param {string} newDate - New inoculation_date (YYYY-MM-DD)
   * @returns {Promise<void>}
   */
  const shiftDate = useCallback(async (batchId, newDate) => {
    try {
      const { error: updateError } = await supabase
        .from('production_batches')
        .update({
          inoculation_date: newDate,
          manually_adjusted: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', batchId)
        .eq('is_shadow', true)

      if (updateError) throw updateError

      toast.success('Batch date updated', {
        description: `Inoculation date: ${new Date(newDate).toLocaleDateString('en-GB')}`
      })
    } catch (err) {
      console.error('[useShadowBatchMutations] Shift date error:', err)
      toast.error('Failed to update date', { description: err.message })
      throw err
    }
  }, [])

  /**
   * Remove a shadow batch
   *
   * @param {string} batchId - UUID of the shadow batch
   * @returns {Promise<void>}
   */
  const removeBatch = useCallback(async (batchId) => {
    try {
      const { error: deleteError } = await supabase
        .from('production_batches')
        .delete()
        .eq('id', batchId)
        .eq('is_shadow', true)

      if (deleteError) throw deleteError

      toast.success('Shadow batch removed')
    } catch (err) {
      console.error('[useShadowBatchMutations] Remove batch error:', err)
      toast.error('Failed to remove batch', { description: err.message })
      throw err
    }
  }, [])

  /**
   * Create a manual shadow batch
   * Useful when the AI hasn't suggested a batch but the manager wants one.
   *
   * @param {string} strainId - UUID of the strain
   * @param {string} weekStart - Target week start date (YYYY-MM-DD)
   * @param {number} blocks - Number of blocks for this batch
   * @returns {Promise<Object>} Created batch object
   */
  const addManualBatch = useCallback(async (strainId, weekStart, blocks) => {
    try {
      // Get strain info for batch code
      const { data: strain, error: strainError } = await supabase
        .from('strains')
        .select('code, name')
        .eq('id', strainId)
        .single()

      if (strainError) throw strainError

      // Generate batch code: SHADOW-{YYMMDD}-{StrainCode}
      const inocDate = new Date(weekStart)
      const yy = String(inocDate.getFullYear()).slice(-2)
      const mm = String(inocDate.getMonth() + 1).padStart(2, '0')
      const dd = String(inocDate.getDate()).padStart(2, '0')
      const batchCode = `SHADOW-${yy}${mm}${dd}-${strain.code}`

      const newBatch = {
        batch_code: batchCode,
        strain_id: strainId,
        total_blocks: blocks,
        current_state: 'planned',
        is_shadow: true,
        approval_status: 'pending',
        demand_week_start: weekStart,
        inoculation_date: weekStart,
        manually_adjusted: true,
        target_kg_fixed: blocks * 0.3, // Estimate 0.3kg per block
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data, error: insertError } = await supabase
        .from('production_batches')
        .insert(newBatch)
        .select()
        .single()

      if (insertError) throw insertError

      toast.success('Manual batch added', {
        description: `${blocks} blocks — ${strain.name}`
      })

      return data
    } catch (err) {
      console.error('[useShadowBatchMutations] Add manual batch error:', err)
      toast.error('Failed to add batch', { description: err.message })
      throw err
    }
  }, [])

  return {
    adjustQuantity,
    shiftDate,
    removeBatch,
    addManualBatch
  }
}

export default useShadowBatchMutations
